/**
 * @module app.um
 */

import { autoinject, observable } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { TransitionalManager } from 'app/shared/lib/transitional/manager';
import { MemberService } from 'app/cd/services/member-service';
import { IMemberSearchFilters } from 'app/cd/services/member-service';
import { RouteConfig, NavigationInstruction } from 'aurelia-router';
import { EssModalCustomElement } from 'app/shared/elements/ess-dialog';
import { validate } from 'app/shared/elements/ess-rule';
import { apiCall } from 'app/shared/lib/api/api';
import { MemberPlanSelect } from 'app/cd/components/member-plan-select';
import { deepClone, getDocumentIcon } from 'app/shared/lib/utilities';
import { CreateAuthForm } from 'app/um/components/auth/create-auth-form';
import { FaxService } from 'app/fm/services/fax-service';
import { downloadFile, openFileInWindow } from 'app/shared/lib/files';
import { I18N } from 'aurelia-i18n';
import { Toaster } from 'app/shared/lib/toaster';
import { guid } from 'app/shared/lib/guid';
import * as CD from 'app/cd/models/essette-api';
import * as UM from 'app/um/models/essette-api';
import * as FM from 'app/fm/models/essette-api';
import { EventAggregator } from 'aurelia-event-aggregator';
import { MemberPlanChangeEvent } from 'app/cd/lib/events';

@autoinject()
export class CreateAuthPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @observable
    public memberPlan: CD.MemberPlan | null = null;

    @observable
    public memberSearchFilters: IMemberSearchFilters = {};

    protected memberAvailableFilters: string[] | null = [
        'active', 'identifier.primary', 'identifier.medicaid', 'name.first', 'name.last', 'birthDate',
        'primaryCarePhysician', 'primaryCarePhysician.name.first', 'primaryCarePhysician.name.last',
        'healthPlan', 'lineOfBusiness'
    ];

    protected faxDocument: FM.FaxDocument | null = null;
    protected getDocumentIcon = getDocumentIcon;

    protected nonMemberPlan: CD.MemberPlan | null = null;
    protected useNonMember: boolean = false;

    private memberId: string | null = null;
    private faxDocumentId: string | null = null;
    private authId: string | null = null;

    protected validateForm = validate;

    protected id: guid = guid();

    ////****************** Element References ***************************************////
    public $modal!: EssModalCustomElement;
    public $memberPlanSelect!: MemberPlanSelect;
    public $form!: CreateAuthForm;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly memberService: MemberService,
        private readonly faxService: FaxService,
        protected transitional: TransitionalManager,
        private readonly i18n: I18N,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        ...rest: any[]) {

        super(...rest);
    }

    protected async activate(params: { memberId: string, faxDocumentId: string }, routeConfig: RouteConfig, navInstruction: NavigationInstruction): Promise<void> {
        if(params.memberId) {
            this.memberId = params.memberId;
        }
        else {
            this.memberPlan = null;
        }

        if(params.faxDocumentId) {
            this.faxDocumentId = params.faxDocumentId;
        }

        this.reload();
    }

    protected attached(): void {
        if(this.memberId) {
            if(this.$memberPlanSelect) {
                this.$memberPlanSelect.clear();
                this.$memberPlanSelect.searchMemberPlan(this.memberId);
            }

            this.memberId = null;
        }
    }

    protected deactivate(): void {
        this.memberPlan = null;
        this.faxDocumentId = null;
        this.faxDocument = null;

        if(this.$memberPlanSelect) {
            this.$memberPlanSelect.clear();
        }

        if(this.$form) {
            this.$form.reset();
        }
    }


    ////****************** Events ***************************************************////

    protected handleDownloadFile(event: MouseEvent): void {
        if(this.faxDocument) {
            const docContentTypeLength = this.faxDocument?.document?.mediaType?.contentType?.length || 0
            if(event.shiftKey || event.ctrlKey || this.shouldView(this.faxDocument?.document?.mediaType?.contentType?.slice(docContentTypeLength - 3) || '')) {
                const childWindow = window.open("", "", 'top=25, left=' + (screen.width / 4) + ',width=1000,height=' + (screen.height - 150));
                if(childWindow) {
                    this.viewDocument(this.faxDocument, childWindow);
                    return;
                }
            }

            this.downloadDocument(this.faxDocument);
        }
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        if(this.faxDocumentId) {
            await this.loadFaxDocument(this.faxDocumentId);
        }
    }

    protected async createAuthSuccess(): Promise<void> {
        if(this.authId) {
            if(this.faxDocument) {
                this.router.navigate(`#/um/auth/${this.authId}/documents/documents/?queueTypeCode=FM.Document&queueId=${this.faxDocument?.queue?.id}&queueTargetId=${this.faxDocument?.document?.id}`);
            }
            else {
                this.router.navigateToRoute('um.auth', { authId: this.authId });
            }
        }
    }

    protected async editNonMember(): Promise<void> {
        this.memberPlan = null;
    }

    protected async selectNonMember(): Promise<void> {
        if(!this.nonMemberPlan) return;
        this.memberPlan = deepClone(this.nonMemberPlan);
        this.events.publish(new MemberPlanChangeEvent(this.memberPlan));
    }

    protected openAuth(auth: UM.Auth): void {
        if(auth) {
            this.router.navigateToRoute('um.auth', { 'authId': auth.id });
        }
    }

    protected async reset(): Promise<void> {
        if(this.faxDocument?.queue) {
            await this.checkinDocument();

            this.router.navigateToRoute('fm.queues.fax', { queueId: this.faxDocument.queue.id }, { replace: true, trigger: true });
        }
        else {
            this.router.navigateToRoute('um.manage-auths.create', {}, { replace: true, trigger: true });
        }
    }

    protected shouldView(extension: string): boolean {
        const allowed = ['pdf', 'jpg', 'jpeg', 'png', 'txt'];
        const extensionCheck = extension || '';
        return allowed.indexOf(extensionCheck) >= 0;
    }

    protected async downloadDocument(faxDocument: FM.FaxDocument): Promise<void> {
        if(!faxDocument?.id) { return; }

        try {
            const blob = await this.faxService.downloadFaxFile(faxDocument.id);
            const fileName = faxDocument.document?.file?.name || 'download';

            downloadFile(blob, fileName);
        }
        catch(error) {
            this.toaster.error().message(this.i18n.tr('ui:messages.errors.download', { what: this.i18n.tr('fm:faxDocument.$short') })).show();
        }
    }

    protected async viewDocument(faxDocument: FM.FaxDocument, childWindow: Window): Promise<void> {
        if(!faxDocument?.id) { return; }

        try {
            const blob = await this.faxService.downloadFaxFile(faxDocument.id);
            openFileInWindow(childWindow, blob);
        }
        catch(error) {
            this.toaster.error().message(this.i18n.tr('ui:messages.errors.download', { what: this.i18n.tr('fm:faxDocument.$short') })).show();
            childWindow.close();
        }
    }

    ////****************** Service Calls ********************************************////
    // [Nick]: This doesn't appear to be being used anywhere - can it be deleted?
    @apiCall
    private async loadMemberPlan(memberPlanId: string): Promise<UM.Member> {
        try {
            this.state.beginLoad();
            const includes: string[] = [
                'primaryPlan',
                'primaryPlan.healthPlan',
                'primaryPlan.lineOfBusiness',
                'primaryCarePhysician',
                'member'
            ];
            const response = await this.memberService.loadMemberPlan(memberPlanId, includes);
            return response.results[0];
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadFaxDocument(faxDocumentId: string): Promise<void> {
        try {
            this.state.beginLoad();
            const includes: string[] = [
                'document',
                'queue',
                'queue.actions',
                'queue.parameters',
                'priority'
            ];
            const response = await this.faxService.loadFaxDocument(faxDocumentId, includes);
            this.faxDocument = response.results[0];
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    public async checkinDocument(): Promise<void> {
        if(!this.faxDocumentId) { return; }

        try {
            this.state.beginLoad();
            await this.faxService.checkInFaxDocument(this.faxDocumentId);
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }

    }
}
